import {Device} from '@fantasmagames/core/src/device/Device';
import {
	IMicroGamingPreloaderConfig,
	LoaderParts,
	LoadingBarColor,
} from '@fantasmagames/core/src/loader/MicrogamingPreloaderV2';
import {MicrogamingPreloaderUtils} from '@fantasmagames/core/src/loader/MicrogamingPreloaderUtils';

export class BaseMicrogamingPreloaderView implements ICustomPreloaderView {
	protected _progressBar: HTMLElement;
	protected _progressBarBack: HTMLElement;

	protected _publisherLogoPortrait: HTMLImageElement;
	protected _publisherLogoLandscape: HTMLImageElement;
	protected _publisherLogo: HTMLImageElement;

	protected _loaderBarMaxWidth: number = 0;
	protected _progress: number = 0;
	protected _scaleFactor: number = 1;
	protected _screenH: number = 0;
	protected _screenW: number = 0;
	protected _parent: HTMLElement;

	protected _loadingBackground: HTMLImageElement | HTMLElement;
	protected _gameLogo: HTMLImageElement;
	protected _fantasmaLogo: HTMLImageElement;
	protected _disclaimer: HTMLImageElement;

	public endLoading(): void {
		while (this._parent.firstChild) {
			this._parent.removeChild(this._parent.firstChild);
		}
	}

	public updateProgress(totalItems: number, current: number): void {
		this._progress = current / totalItems;
		if (this._progressBar) {
			this._progressBar.style.width = Math.ceil(this._loaderBarMaxWidth * this._progress) + 'px';
		}
	}

	public resize(device: Device): void {
		let ow: number = 0;
		let oh: number = 0;

		this._screenW = device.isSamsungNative ? window.outerWidth : window.innerWidth;
		this._screenH = device.isSamsungNative ? window.outerHeight : window.innerHeight;

		if (device.isMobile && !device.iOS && !device.isSamsungNative) {
			ow = window.outerWidth;
			oh = window.outerHeight;

			this._scaleFactor = this._screenH / oh;
		}

		if (this._progressBar && this._progressBarBack) {
			this._loaderBarMaxWidth = this._screenW * 0.8;
			const barHeight = 8;
			const progressBarY = 0.8;
			this._progressBar.style.height =
				this._progressBarBack.style.height = Math.ceil(barHeight * this._scaleFactor) + 'px';
			this._progressBar.style.borderRadius =
				this._progressBarBack.style.borderRadius = Math.ceil(barHeight * 0.5 * this._scaleFactor) + 'px';
			this._progressBar.style.left =
				this._progressBarBack.style.left = Math.ceil((this._screenW - this._loaderBarMaxWidth) * 0.5) + 'px';
			this._progressBar.style.top =
				this._progressBarBack.style.top = Math.ceil(this._screenH * progressBarY) + 'px';

			this._progressBarBack.style.width = Math.ceil(this._loaderBarMaxWidth) + 'px';
			this._progressBar.style.width = Math.ceil(this._loaderBarMaxWidth * this._progress) + 'px';
		}

		const divisor = device.isMobile ? 320 : 640;
		const logoScale = Math.max(0.5, Math.min(this._screenW / divisor, 1)) * this._scaleFactor;

		if (this._loadingBackground) {
			this._loadingBackground.style.width = Math.ceil(this._screenW) + 'px';
			this._loadingBackground.style.height = Math.ceil(this._screenH) + 'px';
		}

		if (this._fantasmaLogo) {
			// Putting our brand name first.
			const centerElement = this._fantasmaLogo;

			centerElement.style.transform = `translateX(-50%) translateY(-50%) scale(${logoScale})`;
			centerElement.style.maxWidth = `${this._screenW - 20}px`;
			centerElement.style.left = Math.ceil(this._screenW * 0.5) + 'px';
			centerElement.style.top = Math.ceil(this._screenH * 0.5) + 'px';

			if (this._publisherLogo) {
				const publisherBrand = this._publisherLogo;

				if (device.isMobile) {
					publisherBrand.style.transform = `translateX(-50%) translateY(-50%) scale(${logoScale * 0.25})`;
				} else {
					publisherBrand.style.transform = `translateX(-50%) translateY(-50%) scale(${logoScale * 0.5})`;
				}

				publisherBrand.style.left = Math.ceil(this._screenW * 0.5) + 'px';
				publisherBrand.style.top = Math.ceil(this._screenH * 0.15) + 'px';
			}
		} else {
			if (this._publisherLogoLandscape && this._publisherLogoPortrait) {
				if (device.isMobile) {
					this._publisherLogoLandscape.style.transform = `translateX(-50%) translateY(-50%) scale(${logoScale * 0.5})`;
				} else {
					this._publisherLogoLandscape.style.transform = `translateX(-50%) translateY(-50%) scale(${logoScale})`;
				}
				this._publisherLogoLandscape.style.left = Math.ceil(this._screenW * 0.5) + 'px';
				this._publisherLogoLandscape.style.top = Math.ceil(this._screenH * 0.5) + 'px';

				this._publisherLogoPortrait.style.transform = `translateX(-50%) translateY(-50%) scale(${logoScale})`;
				this._publisherLogoPortrait.style.left = Math.ceil(this._screenW * 0.5) + 'px';
				this._publisherLogoPortrait.style.top = Math.ceil(this._screenH * 0.45) + 'px';

				if (device.isMobile) {
					this._publisherLogoLandscape.style.display = this._screenW > this._screenH ? null : 'none';
					this._publisherLogoPortrait.style.display = this._screenW > this._screenH ? 'none' : null;
				} else {
					this._publisherLogoLandscape.style.display = null;
					this._publisherLogoPortrait.style.display = 'none';
				}
			}
		}

		if (this._gameLogo) {
			this._gameLogo.style.transform = `translateX(-50%) translateY(-50%) scale(${logoScale / 1.5})`;
			this._gameLogo.style.left = Math.ceil(this._screenW * 0.5) + 'px';
			this._gameLogo.style.top = Math.ceil(this._screenH * 0.45) + 'px';
		}

		if (this._disclaimer) {
			this._disclaimer.style.left = Math.ceil(this._screenW * 0.5) + 'px';
			this._disclaimer.style.top = Math.ceil(this._screenH * 0.95) + 'px';

			const maxScale = Math.max(0.1, Math.min(this._screenW / 640, 1)) * this._scaleFactor;
			this._disclaimer.style.transform = `translateX(-50%) translateY(-50%) scale(${maxScale})`;
		}
	}

	public init(parent: HTMLElement, config: IMicroGamingPreloaderConfig, getResourceUrl: (path: string) => string): void {
		this._parent = parent;

		// Non-asian market.
		if (!config.setDesign) {
			// On fallback to Game Brand, don't show this background.

			if (config.hasPublisherSplash) {

				const bg: HTMLElement = document.createElement('div');
				bg.style.background = 'radial-gradient(rgb(64, 64, 64), rgb(40, 40, 40), rgb(17, 17, 17))';
				bg.id = 'preloaderBg';
				bg.style.width = '1px';
				bg.style.height = '1px';
				bg.style.position = 'absolute';
				bg.style.top = '0px';
				bg.style.left = '0px';

				this._loadingBackground = bg;
				parent.appendChild(this._loadingBackground);
			}
		}

		if (config.contentRequired.indexOf(LoaderParts.PUBLISHER_LOGO) > -1) {
			if (config.setDesign) {
				this._publisherLogoLandscape = MicrogamingPreloaderUtils.createPublisherLogo('PublisherSplash.jpg', getResourceUrl(`${config.publisherName}_PublisherSplash.jpg`), true);
				this._publisherLogoPortrait = MicrogamingPreloaderUtils.createPublisherLogo('PublisherSplashPortrait.jpg', getResourceUrl(`${config.publisherName}_PublisherSplashPortrait.jpg`), true);

				parent.appendChild(this._publisherLogoPortrait);
				parent.appendChild(this._publisherLogoLandscape);
			} else {
				this._publisherLogo = MicrogamingPreloaderUtils.createPublisherLogo('logo.png', getResourceUrl('brandFallback.png'), false);
				parent.appendChild(this._publisherLogo);
			}
		}

		if (config.contentRequired.indexOf(LoaderParts.LOADING_BAR) > -1) {
			this._progressBarBack = MicrogamingPreloaderUtils.createBar('meter-bg-container', 'grey', 'translateX(0%) translateY(-50%)');
			parent.appendChild(this._progressBarBack);

			this._progressBar = MicrogamingPreloaderUtils.createBar('meter-bg-bar', 'white', 'translateX(0%) translateY(-50%)');

			if (config.loadingBarColor === LoadingBarColor.MULTI_COLORED) {
				this._progressBar.style.background = 'linear-gradient(0.25turn, #e66465, #9198e5)';
			}
			parent.appendChild(this._progressBar);
		}

		if (config.contentRequired.indexOf(LoaderParts.GAME) > -1) {
			this._gameLogo = MicrogamingPreloaderUtils.createImage('gameLogo');
			this._gameLogo.src = getResourceUrl('GAME_LOGO.png');
			parent.appendChild(this._gameLogo);
		}

		if (config.contentRequired.indexOf(LoaderParts.STUDIO) > -1) {
			this._fantasmaLogo = MicrogamingPreloaderUtils.createImage('studioLogo');
			this._fantasmaLogo.src = getResourceUrl('Fantasma_logo.png');
			parent.appendChild(this._fantasmaLogo);
		}

		if (config.contentRequired.indexOf(LoaderParts.DISCLAIMER) > -1) {
			this._disclaimer = MicrogamingPreloaderUtils.createDisclaimer();
			parent.appendChild(this._disclaimer);
		}
	}
}

export interface ICustomPreloaderView {
	init(parent: HTMLElement, config: IMicroGamingPreloaderConfig, getResourceUrl: (path: string) => string): void;

	endLoading(): void;

	updateProgress(totalItems: number, current: number): void;

	resize(device: Device): void;
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/loader/BaseMicorgamingPreloaderView.ts